import { useCallback, useEffect, useState } from "react"
import {
  isLegacyAddress,
  isNativeSegwitAddress,
  RevealDetails,
} from "@mezo/sdk"
import { PayloadAction } from "@reduxjs/toolkit"
import { useInterval } from "usehooks-ts"
import { useAccount } from "wagmi"

import {
  checkBitcoinDepositedAmount,
  generateBitcoinDepositDetails,
  setDepositAmount,
  useDappDispatch,
} from "#/redux-state"
import {
  BITCOIN_MIN_DEPOSIT,
  BTC_DEPOSIT_CONFIRMATION_INTERVAL,
  ETHEREUM_NETWORK,
  TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS,
} from "#/shared/constants"
import {
  convertBitcoinToSatoshi,
  convertSatoshiToBitcoin,
} from "#/shared/utils"

import { isPassportConnector } from "./wagmi"

export const BITCOIN_MIN_DEPOSIT_SATOSHI =
  convertBitcoinToSatoshi(BITCOIN_MIN_DEPOSIT)

export const useBitcoinRecoveryAddress = () => {
  const { connector } = useAccount()
  const [address, setAddress] = useState<string | null>(null)

  useEffect(() => {
    const fetchBitcoinAddress = async () => {
      if (!connector) {
        setAddress(null)
        return
      }

      if (isPassportConnector(connector)) {
        // TODO: Set correct type. We need to export the PassportWagmiConnector type.
        // @ts-expect-error adjust types to handle bitcoin wallet wrappers
        let bitcoinRecoveryAddress = await connector?.getBitcoinAddress()

        if (
          !isLegacyAddress(bitcoinRecoveryAddress, ETHEREUM_NETWORK) &&
          !isNativeSegwitAddress(bitcoinRecoveryAddress, ETHEREUM_NETWORK)
        ) {
          // Only P2PKH or P2WPKH address can be used as recovery address in tBTC
          // v2.
          bitcoinRecoveryAddress = TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS
        }

        setAddress(bitcoinRecoveryAddress)
        return
      }

      setAddress(TBTC_DEPOSIT_BITCOIN_RECOVERY_ADDRESS)
    }

    fetchBitcoinAddress()
  }, [connector])

  return address
}

export const useBitcoinBridgeDepositDetails = () => {
  const dispatch = useDappDispatch()

  const [depositDetails, setDepositDetails] = useState<RevealDetails | null>(
    null,
  )

  const bitcoinRecoveryAddress = useBitcoinRecoveryAddress()

  useEffect(() => {
    const fetchBitcoinBridgeDepositDetails = async () => {
      if (!bitcoinRecoveryAddress) return

      const { payload: details } = await dispatch(
        generateBitcoinDepositDetails({
          bitcoinRecoveryAddress,
        }),
      )

      if (!details) {
        setDepositDetails(null)
        return
      }

      setDepositDetails(details as RevealDetails)
    }

    fetchBitcoinBridgeDepositDetails()
  }, [dispatch, bitcoinRecoveryAddress])

  return depositDetails
}

export const useDetectBitcoinFunds = (depositAddress: string | null) => {
  const dispatch = useDappDispatch()

  const [detectedDepositAmount, setDetectedDepositAmount] = useState<bigint>(0n)
  const [hasDeposited, setHasDeposited] = useState(false)
  const [sentInsufficientFunds, setSentInsufficientFunds] = useState(false)

  const handleStatusCheck = useCallback(async () => {
    if (!depositAddress) return

    const { payload: amountDepositedInSatoshi } = (await dispatch(
      checkBitcoinDepositedAmount({ address: depositAddress }),
    )) as PayloadAction<bigint>

    setDetectedDepositAmount(amountDepositedInSatoshi)

    if (amountDepositedInSatoshi === 0n) return

    dispatch(
      setDepositAmount(
        convertSatoshiToBitcoin(amountDepositedInSatoshi).toString(),
      ),
    )

    if (amountDepositedInSatoshi < BigInt(BITCOIN_MIN_DEPOSIT_SATOSHI)) {
      setSentInsufficientFunds(true)
    } else {
      setHasDeposited(true)
    }
  }, [depositAddress, dispatch])

  useInterval(
    handleStatusCheck,
    !hasDeposited && depositAddress ? BTC_DEPOSIT_CONFIRMATION_INTERVAL : null,
  )

  return {
    hasDeposited,
    sentInsufficientFunds,
    detectedDepositAmount,
  }
}
